"use client"

/**
 * Product route sync — same behaviour as `@exxatdesignux/ui`, with one app rule:
 * built-in Prism suite slugs (`/compliance`, `/curriculum-mapping`, …) resolve
 * to shipped products even when tenant custom products mirror those slugs.
 */

import * as React from "react"
import { useLocation, useNavigate } from "react-router"
import {
  useAppStore,
  hasConfiguredCustomProducts,
  productSlug,
  type Product,
} from "@exxatdesignux/product-framework/app-store"
import { productRouteSlug } from "@exxatdesignux/product-framework/product-routing"
import { usePersistHydrated } from "@exxatdesignux/ui/hooks/use-persist-hydrated"

import { useProduct } from "@/contexts/product-context"
import { isPeopleWorkspacePath } from "@/lib/demo-shell-routes"
import { openExamManagementHostedAppIfEntitled } from "@/lib/exam-management-hosted"
import { openSurveysHostedAppIfEntitled } from "@/lib/surveys-hosted"
import { oneSiteDemoLandingPath } from "@/lib/one-site-demo"
import { resolveProductFromPathPreferringBuiltInPrism } from "@/lib/prism-document-theme"
import {
  isStudentHomeSession,
  STUDENT_EXXAT_ONE_PRODUCT,
} from "@/lib/student-exxat-one-home"

/** Safety net when a redirect chain never settles or navigation is a no-op. */
const SWITCHING_STUCK_MS = 4000

function productLandingSegment(product: Product): "home" | "dashboard" {
  if (isStudentHomeSession() && product === STUDENT_EXXAT_ONE_PRODUCT) return "home"
  return "dashboard"
}

export function ProductRouteSync() {
  const {
    product,
    setProduct,
    customProducts,
    activeCustomIndex,
    setActiveCustomIndex,
  } = useProduct()
  const location = useLocation()
  const navigate = useNavigate()
  const switching = useAppStore(s => s.productSwitching)
  const setProductSwitching = useAppStore(s => s.setProductSwitching)
  const hydrated = usePersistHydrated()

  React.useEffect(() => {
    if (!hydrated) return

    let deferredNavigation = false

    const segment = location.pathname.replace(/^\/+/, "").split("/", 1)[0] ?? ""

    // Student and Faculty Management are workspace iframes, not a People product.
    if (isPeopleWorkspacePath(location.pathname)) {
      if (product === "exxat-people") setProduct("exxat-prism")
      setProductSwitching(false)
      return
    }

    // Store → URL is in flight. Do not let a stale path clobber the target
    // product until the new root lands (or a dashboard redirect under it).
    if (switching) {
      const targetSlug = productRouteSlug(product, customProducts, activeCustomIndex)
      const onTargetRoot =
        location.pathname === `/${targetSlug}` ||
        location.pathname.startsWith(`/${targetSlug}/`)
      if (onTargetRoot) {
        setProductSwitching(false)
      }
      return
    }

    if (segment === "custom") {
      if (!hasConfiguredCustomProducts(customProducts)) {
        if (product !== "exxat-prism") setProduct("exxat-prism")
        deferredNavigation = true
        navigate(`/${productSlug("exxat-prism")}/dashboard`, { replace: true })
      } else {
        const targetSlug = productRouteSlug(
          "exxat-custom",
          customProducts,
          activeCustomIndex,
        )
        const tail = location.pathname.replace(/^\/custom\/?/, "")
        deferredNavigation = true
        navigate(`/${targetSlug}/${tail || "dashboard"}${location.search}${location.hash}`, {
          replace: true,
        })
      }
    } else {
      const resolved = resolveProductFromPathPreferringBuiltInPrism(
        location.pathname,
        customProducts,
      )

      if (resolved == null) {
        setProductSwitching(false)
        return
      }

      if (
        resolved.product === "exxat-custom" &&
        !hasConfiguredCustomProducts(customProducts)
      ) {
        if (product !== "exxat-prism") setProduct("exxat-prism")
        deferredNavigation = true
        navigate(`/${productSlug("exxat-prism")}/dashboard`, { replace: true })
      } else {
        if (
          resolved.customIndex !== undefined &&
          resolved.customIndex !== activeCustomIndex
        ) {
          setActiveCustomIndex(resolved.customIndex)
        }

        if (resolved.product !== product) {
          setProduct(resolved.product)
        }

        if (
          product === "exxat-custom" &&
          resolved.product === "exxat-custom" &&
          hasConfiguredCustomProducts(customProducts)
        ) {
          const expectedSlug = productRouteSlug(
            "exxat-custom",
            customProducts,
            resolved.customIndex ?? activeCustomIndex,
          )
          if (segment !== expectedSlug) {
            const tail = location.pathname.replace(/^\/[^/]+\/?/, "")
            deferredNavigation = true
            navigate(`/${expectedSlug}/${tail || "dashboard"}${location.search}${location.hash}`, {
              replace: true,
            })
          }
        }

        if (!deferredNavigation) {
          setProductSwitching(false)
        }
      }
    }
  }, [
    activeCustomIndex,
    customProducts,
    hydrated,
    location,
    navigate,
    product,
    setActiveCustomIndex,
    setProduct,
    setProductSwitching,
    switching,
  ])

  React.useEffect(() => {
    if (!hydrated || !switching) return

    const timer = window.setTimeout(() => {
      setProductSwitching(false)
    }, SWITCHING_STUCK_MS)

    return () => window.clearTimeout(timer)
  }, [hydrated, setProductSwitching, switching])

  return null
}

export function useProductDashboardHref(): string {
  const { product, customProducts, activeCustomIndex } = useProduct()
  const demoLanding = oneSiteDemoLandingPath(product)
  if (demoLanding) return demoLanding
  const slug = productRouteSlug(product, customProducts, activeCustomIndex)
  return `/${slug}/${productLandingSegment(product)}`
}

export function useProductOrganizationSettingsHref(): string {
  const { product, customProducts, activeCustomIndex } = useProduct()
  const slug = productRouteSlug(product, customProducts, activeCustomIndex)
  return `/${slug}/settings`
}

export function useProductSwitch() {
  const { setProduct, setActiveCustomIndex } = useProduct()
  const navigate = useNavigate()
  const setProductSwitching = useAppStore(s => s.setProductSwitching)
  return React.useCallback(
    (next: Product, customIndex?: number) => {
      if (next === "exxat-exam-management" && openExamManagementHostedAppIfEntitled()) {
        return
      }
      if (next === "exxat-surveys" && openSurveysHostedAppIfEntitled()) {
        return
      }
      const demoLanding = oneSiteDemoLandingPath(next)
      const state = useAppStore.getState()
      const idx =
        customIndex ??
        (next === "exxat-custom" ? state.activeCustomIndex : 0)
      const slug = productRouteSlug(next, state.customProducts, idx)
      setProductSwitching(true)
      if (customIndex !== undefined) {
        setActiveCustomIndex(customIndex)
      }
      setProduct(next)
      navigate(demoLanding ?? `/${slug}/${productLandingSegment(next)}`)
    },
    [navigate, setActiveCustomIndex, setProduct, setProductSwitching],
  )
}
